import type {
  ForecastConfig,
  ForecastMethod,
  ForecastPoint,
  TrendGrain,
  TrendPoint,
} from './types';

const MIN_VARIANCE_THRESHOLD = 1e-9;
const MIN_FORECAST_POINTS = 6;
const MIN_FORECAST_CALENDAR_MONTHS = 6;

const clampLower = (value: number, clampLowerToZero: boolean) =>
  clampLowerToZero ? Math.max(0, value) : value;

const toDate = (timestamp: number) => new Date(timestamp);

const addStep = (date: Date, grain: TrendGrain) => {
  const next = new Date(date.getTime());
  if (grain === 'day') {
    next.setDate(next.getDate() + 1);
    return next;
  }
  if (grain === 'month') {
    next.setMonth(next.getMonth() + 1);
    return next;
  }
  if (grain === 'quarter') {
    next.setMonth(next.getMonth() + 3);
    return next;
  }
  next.setFullYear(next.getFullYear() + 1);
  return next;
};

const formatBucket = (date: Date, grain: TrendGrain) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (grain === 'day') {
    const label = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { bucketKey: label, label };
  }
  if (grain === 'month') {
    const label = `${year}-${String(month).padStart(2, '0')}`;
    return { bucketKey: label, label };
  }
  if (grain === 'quarter') {
    const quarter = Math.floor((month - 1) / 3) + 1;
    return { bucketKey: `${year}-Q${quarter}`, label: `Q${quarter} ${year}` };
  }

  return { bucketKey: `${year}`, label: `${year}` };
};

const buildFutureBuckets = (lastTimestamp: number, horizon: number, grain: TrendGrain) => {
  const buckets: Array<{ bucketKey: string; label: string; timestamp: number }> = [];
  let cursor = toDate(lastTimestamp);

  for (let step = 1; step <= horizon; step += 1) {
    cursor = addStep(cursor, grain);
    const bucket = formatBucket(cursor, grain);
    buckets.push({ ...bucket, timestamp: cursor.getTime() });
  }

  return buckets;
};

const withHistory = (history: TrendPoint[]): ForecastPoint[] =>
  history.map(point => ({
    bucketKey: point.bucketKey,
    label: point.label,
    metricValue: point.metricValue,
    forecast: undefined,
    upper: undefined,
    lower: undefined,
    isFuture: false,
    timestamp: point.timestamp,
  }));

const getCalendarMonthIndex = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.getFullYear() * 12 + date.getMonth();
};

export const getCalendarMonthSpan = (history: TrendPoint[]): number => {
  if (!history.length) return 0;
  const monthIndexes = history.map(point => getCalendarMonthIndex(point.timestamp));
  const minMonth = Math.min(...monthIndexes);
  const maxMonth = Math.max(...monthIndexes);
  return maxMonth - minMonth + 1;
};

export const hasMinimumProjectionData = (
  history: TrendPoint[],
  minPoints = MIN_FORECAST_POINTS,
  minCalendarMonths = MIN_FORECAST_CALENDAR_MONTHS
): boolean => {
  if (history.length < minPoints) return false;
  return getCalendarMonthSpan(history) >= minCalendarMonths;
};

const mean = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;

const std = (values: number[]) => {
  if (values.length < 2) return 0;
  const m = mean(values);
  const variance = values.reduce((sum, value) => sum + (value - m) ** 2, 0) / (values.length - 1);
  return Math.sqrt(Math.max(variance, 0));
};

const buildNaiveForecast = (
  history: TrendPoint[],
  config: ForecastConfig,
  grain: TrendGrain
): ForecastPoint[] => {
  if (!history.length) return [];

  const base = withHistory(history);
  const last = history[history.length - 1];
  const lastValue = last.metricValue;
  const historyValues = history.map(point => point.metricValue);
  const sigma = std(historyValues);
  const safetyBand = Math.max(sigma * 0.15, Math.abs(lastValue) * 0.05, 1);

  const futureBuckets = buildFutureBuckets(last.timestamp, config.horizon, grain);
  const future = futureBuckets.map(bucket => {
    const upper = lastValue + config.confidenceZ * safetyBand;
    const lower = clampLower(lastValue - config.confidenceZ * safetyBand, config.clampLowerToZero);

    return {
      bucketKey: bucket.bucketKey,
      label: bucket.label,
      metricValue: undefined,
      forecast: lastValue,
      upper,
      lower,
      isFuture: true,
      timestamp: bucket.timestamp,
    } as ForecastPoint;
  });

  return [...base, ...future];
};

export const buildLinearForecast = (
  history: TrendPoint[],
  config: ForecastConfig,
  grain: TrendGrain
): ForecastPoint[] => {
  if (history.length < 6) {
    return buildNaiveForecast(history, config, grain);
  }

  const n = history.length;
  const xValues = history.map((_, index) => index + 1);
  const yValues = history.map(point => point.metricValue);
  const xBar = mean(xValues);
  const yBar = mean(yValues);

  const sxx = xValues.reduce((acc, x) => acc + (x - xBar) ** 2, 0);
  if (sxx < MIN_VARIANCE_THRESHOLD) {
    return buildNaiveForecast(history, config, grain);
  }

  const sxy = xValues.reduce((acc, x, index) => acc + (x - xBar) * (yValues[index] - yBar), 0);
  const slope = sxy / sxx;
  const intercept = yBar - slope * xBar;

  const residuals = yValues.map((value, index) => value - (intercept + slope * xValues[index]));
  const residualStd = std(residuals);
  if (residualStd < MIN_VARIANCE_THRESHOLD) {
    return buildNaiveForecast(history, config, grain);
  }

  const base = withHistory(history);
  const last = history[history.length - 1];
  const futureBuckets = buildFutureBuckets(last.timestamp, config.horizon, grain);

  const future = futureBuckets.map((bucket, index) => {
    const xh = n + index + 1;
    const forecast = intercept + slope * xh;
    const se = residualStd * Math.sqrt(1 + 1 / n + (xh - xBar) ** 2 / sxx);
    const margin = config.confidenceZ * se;
    const upper = forecast + margin;
    const lower = clampLower(forecast - margin, config.clampLowerToZero);

    return {
      bucketKey: bucket.bucketKey,
      label: bucket.label,
      metricValue: undefined,
      forecast,
      upper,
      lower,
      isFuture: true,
      timestamp: bucket.timestamp,
    } as ForecastPoint;
  });

  return [...base, ...future];
};

export const buildHoltForecast = (
  history: TrendPoint[],
  config: ForecastConfig,
  grain: TrendGrain
): ForecastPoint[] => {
  if (history.length < 6) {
    return buildNaiveForecast(history, config, grain);
  }

  const alpha = 0.35;
  const beta = 0.2;
  let level = history[0].metricValue;
  let trend = history[1] ? history[1].metricValue - history[0].metricValue : 0;

  const fitted: number[] = [];

  history.forEach((point, index) => {
    if (index === 0) {
      fitted.push(level);
      return;
    }

    const prevLevel = level;
    level = alpha * point.metricValue + (1 - alpha) * (level + trend);
    trend = beta * (level - prevLevel) + (1 - beta) * trend;
    fitted.push(level + trend);
  });

  const residuals = history.map(
    (point, index) => point.metricValue - (fitted[index] ?? point.metricValue)
  );
  const sigma = std(residuals);

  const base = withHistory(history);
  const last = history[history.length - 1];
  const futureBuckets = buildFutureBuckets(last.timestamp, config.horizon, grain);

  const future = futureBuckets.map((bucket, index) => {
    const h = index + 1;
    const forecast = level + h * trend;
    const margin = config.confidenceZ * sigma * Math.sqrt(h);
    const upper = forecast + margin;
    const lower = clampLower(forecast - margin, config.clampLowerToZero);

    return {
      bucketKey: bucket.bucketKey,
      label: bucket.label,
      metricValue: undefined,
      forecast,
      upper,
      lower,
      isFuture: true,
      timestamp: bucket.timestamp,
    } as ForecastPoint;
  });

  return [...base, ...future];
};

export const buildGrowthForecast = (
  history: TrendPoint[],
  config: ForecastConfig,
  grain: TrendGrain
): ForecastPoint[] => {
  if (history.length < 6) {
    return buildNaiveForecast(history, config, grain);
  }

  const safeSeries = history
    .map(point => point.metricValue)
    .filter(value => Number.isFinite(value) && value > 0);

  if (safeSeries.length < 6) {
    return buildNaiveForecast(history, config, grain);
  }

  const returns: number[] = [];
  for (let i = 1; i < safeSeries.length; i += 1) {
    returns.push(Math.log(safeSeries[i] / safeSeries[i - 1]));
  }

  const mu = mean(returns);
  const sigma = std(returns);
  const last = history[history.length - 1];
  const base = withHistory(history);
  const futureBuckets = buildFutureBuckets(last.timestamp, config.horizon, grain);

  const future = futureBuckets.map((bucket, index) => {
    const h = index + 1;
    const forecast = last.metricValue * Math.exp(h * mu);
    const upper = forecast * Math.exp(config.confidenceZ * sigma * Math.sqrt(h));
    const lowerRaw = forecast * Math.exp(-config.confidenceZ * sigma * Math.sqrt(h));
    const lower = clampLower(lowerRaw, config.clampLowerToZero);

    return {
      bucketKey: bucket.bucketKey,
      label: bucket.label,
      metricValue: undefined,
      forecast,
      upper,
      lower,
      isFuture: true,
      timestamp: bucket.timestamp,
    } as ForecastPoint;
  });

  return [...base, ...future];
};

export const buildForecast = (
  history: TrendPoint[],
  config: ForecastConfig,
  grain: TrendGrain
): ForecastPoint[] => {
  if (!hasMinimumProjectionData(history)) {
    return withHistory(history);
  }

  const method: ForecastMethod = config.method;
  if (method === 'holt') return buildHoltForecast(history, config, grain);
  if (method === 'growth') return buildGrowthForecast(history, config, grain);
  return buildLinearForecast(history, config, grain);
};
